import React, { useState } from "react";
import {
  Box,
  Flex,
  VStack,
  Text,
  Link,
  Icon,
  Collapse,
  IconButton,
  Image,
} from "@chakra-ui/react";
import { NavLink, Link as RouterLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import {
  AiOutlineAppstore,
  AiOutlineDatabase,
  AiOutlineLineChart,
  AiOutlineDollarCircle,
  AiOutlineDown,
  AiOutlineRight,
  AiOutlineMenu,
  AiOutlineClose,
  AiOutlineDesktop,
  AiOutlineWarning,
  AiOutlineMonitor,
  AiOutlineBug,
} from "react-icons/ai";

import { SiMongodb, SiMysql } from "react-icons/si";
import { DiMsqlServer } from "react-icons/di";

const logoUrl =
  "https://s3-contel-imagens-aplicacao.s3.us-east-1.amazonaws.com/nogle/Brand.svg";

const commonFlexStyles = (isCollapsed) => ({
  align: "center",
  h: "48px",
  borderRadius: "md",
  cursor: "pointer",
  transition: "background-color .2s ease",
  _hover: { bg: "gray.600", color: "white" },
  w: "full",
  justifyContent: isCollapsed ? "center" : "flex-start",
  px: isCollapsed ? 2 : 4,
  whiteSpace: "nowrap",
});

const NavItem = ({ icon, children, isCollapsed, to = "#", ...rest }) => (
  <Link
    as={NavLink}
    to={to}
    end
    _hover={{ textDecoration: "none" }}
    w="full"
    _activeLink={{ color: "secondary" }}
  >
    {({ isActive }) => (
      <Flex
        {...commonFlexStyles(isCollapsed)}
        position="relative"
        {...rest}
        bg={isActive ? "gray.700" : "transparent"}
        color={isActive ? "secondary" : "white"}
      >
        <Box
          position="absolute"
          left="0"
          h="80%"
          w="2px"
          bg="secondary"
          borderRadius="full"
          opacity={isActive ? 1 : 0}
          transition="opacity 0.2s ease-in-out"
          top="50%"
          transform="translateY(-50%)"
        />

        {icon && <Icon fontSize="20px" as={icon} ml={isCollapsed ? 0 : 0} />}

        <AnimatePresence initial={false}>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              style={{
                width: "100%",
                whiteSpace: "nowrap",
                marginLeft: "1rem",
                overflow: "hidden",
              }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </Flex>
    )}
  </Link>
);

const MenuToggle = ({ icon, children, isCollapsed, onClick }) => (
  <Flex {...commonFlexStyles(isCollapsed)} onClick={onClick} color="white">
    {icon && <Icon fontSize="20px" as={icon} ml={isCollapsed ? 0 : 0} />}

    <AnimatePresence initial={false}>
      {!isCollapsed && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          style={{
            width: "100%",
            whiteSpace: "nowrap",
            marginLeft: "1rem",
            overflow: "hidden",
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  </Flex>
);

const Sidebar = ({ isCollapsed, onToggle }) => {
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [erpNestedMenuOpen, setErpNestedMenuOpen] = useState(false);
  const [dbMenuOpen, setDbMenuOpen] = useState(false);
  const sidebarWidth = isCollapsed ? "80px" : "280px";

  return (
    <Box
      as={motion.div}
      animate={{ width: sidebarWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      overflowX="hidden" 
      bg="surface"
      borderRight="1px"
      borderColor="gray.700"
      h="100vh"
      position="fixed"
      zIndex={10}
    >
      <Flex
        h="20"
        alignItems="center"
        px={isCollapsed ? 2 : 4}
        justifyContent={isCollapsed ? "center" : "space-between"}
      >
        <AnimatePresence initial={false}>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "100px" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <RouterLink to="/">
                <Image
                  src={logoUrl}
                  alt="Nogle Logo"
                  w="100px"
                  cursor="pointer"
                />
              </RouterLink>
            </motion.div>
          )}
        </AnimatePresence>
        <IconButton
          aria-label="Toggle Menu"
          icon={isCollapsed ? <AiOutlineMenu /> : <AiOutlineClose />}
          variant="ghost"
          onClick={onToggle}
          fontSize="xl"
          _hover={{ bg: "gray.600", color: "secondary" }}
          color="secondary"
        />
      </Flex>

      <VStack spacing={1} align="stretch" as="nav" px={isCollapsed ? 2 : 4}>
        <Box>
          <MenuToggle
            icon={AiOutlineAppstore}
            isCollapsed={isCollapsed}
            onClick={() => !isCollapsed && setProductMenuOpen(!productMenuOpen)}
          >
            <Flex w="100%" justifyContent="space-between" alignItems="center">
              <Text>Monitor de Produtos</Text>
              {!isCollapsed && (
                <Icon
                  as={productMenuOpen ? AiOutlineDown : AiOutlineRight}
                  strokeWidth="50"
                  color="gray.400"
                />
              )}
            </Flex>
          </MenuToggle>

          <Collapse in={productMenuOpen && !isCollapsed}>
            <Box
              pl={4}
              pt={1}
              pb={1}
              ml={4}
              borderLeft="1px solid"
              borderColor="gray.700"
            >
              <VStack align="stretch" spacing={1}>
                <Box>
                  <MenuToggle
                    icon={AiOutlineDesktop}
                    isCollapsed={isCollapsed}
                    onClick={() =>
                      !isCollapsed && setErpNestedMenuOpen(!erpNestedMenuOpen)
                    }
                  >
                    <Flex
                      w="100%"
                      justifyContent="space-between"
                      alignItems="center"
                      pl={1}
                    >
                      <Text>ERP</Text>
                      {!isCollapsed && (
                        <Icon
                          as={
                            erpNestedMenuOpen ? AiOutlineDown : AiOutlineRight
                          }
                          strokeWidth="50"
                          color="gray.400"
                        />
                      )}
                    </Flex>
                  </MenuToggle>

                  <Collapse in={erpNestedMenuOpen && !isCollapsed}>
                    <Box
                      pt={1}
                      pl={8}
                      ml={3}
                      borderLeft="1px solid"
                      borderColor="gray.700"
                    >
                      <VStack align="stretch" spacing={1}>
                        <NavItem
                          isCollapsed={isCollapsed}
                          to="/panel/erp/screens"
                          pl={2}
                          icon={AiOutlineMonitor}
                        >
                          <Text>Uso de telas</Text>
                        </NavItem>
                        <NavItem
                          isCollapsed={isCollapsed}
                          to="/panel/erp"
                          pl={2}
                          icon={AiOutlineBug}
                        >
                          <Text>Erros</Text>
                        </NavItem>
                      </VStack>
                    </Box>
                  </Collapse>
                </Box>
              </VStack>
            </Box>
          </Collapse>
        </Box>
        <Box>
          <MenuToggle
            icon={AiOutlineDatabase}
            isCollapsed={isCollapsed}
            onClick={() => !isCollapsed && setDbMenuOpen(!dbMenuOpen)}
          >
            <Flex w="100%" justifyContent="space-between" alignItems="center">
              <Text>Banco de Dados</Text>
              {!isCollapsed && (
                <Icon
                  as={dbMenuOpen ? AiOutlineDown : AiOutlineRight}
                  strokeWidth="50"
                  color="gray.400"
                />
              )}
            </Flex>
          </MenuToggle>

          <Collapse in={dbMenuOpen && !isCollapsed}>
            <Box
              pt={1}
              pl={8}
              ml={3}
              borderLeft="1px solid"
              borderColor="gray.700"
            >
              <VStack align="stretch" spacing={1}>
                <NavItem
                  isCollapsed={isCollapsed}
                  to="/monitor/sql-server"
                  icon={DiMsqlServer}
                >
                  <Text>SQL Server</Text>
                </NavItem>

                <NavItem
                  isCollapsed={isCollapsed}
                  to="/monitor/mysql"
                  icon={SiMysql}
                >
                  <Text>MySQL</Text>
                </NavItem>

                <NavItem
                  isCollapsed={isCollapsed}
                  to="/monitor/mongodb"
                  icon={SiMongodb}
                >
                  <Text>MongoDB</Text>
                </NavItem>
              </VStack>
            </Box>
          </Collapse>
        </Box>

        <NavItem
          icon={AiOutlineLineChart}
          isCollapsed={isCollapsed}
          to="/panel/erp/comercial"
        >
          <Text>Comercial</Text>
        </NavItem>
        <NavItem
          icon={AiOutlineDollarCircle}
          isCollapsed={isCollapsed}
          to="/panel/erp/financeiro"
        >
          <Text>Financeiro</Text>
        </NavItem>
      </VStack>
    </Box>
  );
};

export default Sidebar;
